import { useCallback, useEffect, useMemo, useState } from 'react';

import type { UiReviewQueue, UiReviewQueueItem } from '@nightshift/ui-types';

import { Empty, Kicker, RefreshError, Spinner } from '../components/primitives.tsx';
import { api } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import { useTimeFormat } from '../lib/display.ts';
import { absoluteTime, observedTime } from '../lib/format.ts';
import { createLatestRequest } from '../lib/latest-request.ts';
import {
  batchApprovals,
  batchOutcomeMessage,
  bounceLabel,
  needsCloseLook,
  reviewerLabel,
  reviewRiskSummary,
  sensitiveLabel,
  sizeLabel,
  type ApprovalOutcome,
  type ApprovalRequest,
} from '../lib/review-queue.ts';
import type { ViewProps } from './board.tsx';
import './sheet.css';
import './review-queue.css';

/**
 * Every ticket waiting for a person in Human Review, across the projects this session can see
 * (narrowed to the top bar's project when one is chosen), longest wait first.
 *
 * Each row carries the risk a person triages on: how big the change is, whether it touches
 * anything sensitive, how often it bounced and what the AI reviewers said. Approve is the same
 * command as the drawer's, sent with the version this list showed. Approving several at once
 * asks once, then sends each ticket's own version, so a ticket that moved since the list loaded
 * is refused rather than approved unseen.
 */
export function ReviewQueueView({ onOpenTicket }: ViewProps) {
  const live = useLive();
  const store = useStore();
  const format = useTimeFormat();
  const [queue, setQueue] = useState<UiReviewQueue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [busy, setBusy] = useState<ReadonlySet<number>>(() => new Set());
  // The approvals the confirmation was asked about, frozen when it opened: a refresh while it is
  // open must not swap in a version the person never saw.
  const [pending, setPending] = useState<ApprovalRequest[] | null>(null);
  const [latest] = useState(createLatestRequest);
  const poke = `${live.board?.generatedAt ?? 0}:${live.snapshot?.revision ?? 0}`;

  const load = useCallback(async () => {
    const request = latest.begin();
    try {
      const next = await api.reviewQueue();
      if (!latest.isCurrent(request)) return;
      setQueue(next);
      setLoadedAt(Date.now());
      setError(null);
    } catch (caught) {
      if (!latest.isCurrent(request)) return;
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [latest]);

  useEffect(() => {
    void load();
  }, [load, poke]);
  useEffect(() => () => latest.cancel(), [latest]);

  const items = useMemo(
    () => (queue?.items ?? []).filter((item) => live.projectSlug === null || item.projectSlug === live.projectSlug),
    [queue, live.projectSlug],
  );

  // A ticket that left the list (approved elsewhere, sent back) must not stay selected.
  useEffect(() => {
    setSelected((current) => {
      const present = new Set(items.map((item) => item.ticketId));
      const kept = [...current].filter((id) => present.has(id));
      return kept.length === current.size ? current : new Set(kept);
    });
  }, [items]);

  async function approve(requests: readonly ApprovalRequest[]): Promise<ApprovalOutcome[]> {
    const ids = items.filter((item) => requests.some((row) => row.key === item.key && row.project === item.projectSlug)).map((item) => item.ticketId);
    setBusy(new Set(ids));
    const outcomes: ApprovalOutcome[] = [];
    // One at a time, each with its own version. A batch endpoint would have to decide what a
    // half-failed batch means; this way every ticket gets exactly the answer its own Approve
    // would have got.
    for (const row of requests) {
      try {
        const result = await api.approve(row.key, row.version, row.project);
        outcomes.push({ key: row.key, ok: result.ok, message: result.message });
      } catch (caught) {
        outcomes.push({ key: row.key, ok: false, message: caught instanceof Error ? caught.message : String(caught) });
      }
    }
    setBusy(new Set());
    setSelected(new Set());
    setPending(null);
    await load();
    void store.refetch();
    return outcomes;
  }

  async function approveOne(item: UiReviewQueueItem): Promise<void> {
    const [outcome] = await approve([{ key: item.key, project: item.projectSlug, version: item.version }]);
    if (!outcome) return;
    store.toast(outcome.ok ? 'good' : 'bad', outcome.ok ? `${item.key} approved — landing now` : `${item.key} not approved — ${outcome.message}`);
  }

  async function approveSelected(requests: readonly ApprovalRequest[]): Promise<void> {
    const outcomes = await approve(requests);
    const summary = batchOutcomeMessage(outcomes);
    store.toast(summary.tone, summary.message);
  }

  if (error && !queue) {
    return (
      <Empty
        title="The review queue could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }
  if (!queue) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', flex: 1 }}>
        <Spinner size={20} />
      </div>
    );
  }

  const allSelected = items.length > 0 && items.every((item) => selected.has(item.ticketId));
  const working = busy.size > 0;

  return (
    <div className="sheet review-queue" aria-busy={working} data-ready="1">
      <div className="sheet-bar">
        <Kicker count={items.length}>Awaiting human review</Kicker>
        <span className="sheet-note">Longest wait first. Approve hands a ticket to the landing loop, exactly as the drawer does.</span>
        <button
          type="button"
          className="btn btn-pink btn-sm review-queue-batch"
          disabled={selected.size === 0 || working}
          onClick={() => setPending(batchApprovals(items, selected))}
        >
          Approve selected{selected.size > 0 ? ` (${selected.size})` : ''}
        </button>
      </div>
      {error ? <RefreshError what="The review queue" error={error} since={loadedAt} now={live.tick} onRetry={() => void load()} /> : null}
      {pending ? (
        <div className="review-queue-confirm rise" role="alertdialog" aria-labelledby="review-queue-confirm-text">
          <p id="review-queue-confirm-text">
            Approve {pending.length === 1 ? 'this ticket' : `these ${pending.length} tickets`}?{' '}
            {pending.map((row) => row.key).join(', ')} will each go to the landing loop, which rebases, waits for CI and
            merges. A ticket that changed since you selected it is refused, not approved.
          </p>
          <div className="review-queue-confirm-actions">
            <button type="button" className="btn btn-pink" disabled={working} onClick={() => void approveSelected(pending)}>
              {working ? 'Approving…' : `Approve ${pending.length}`}
            </button>
            <button type="button" className="btn btn-ghost" disabled={working} onClick={() => setPending(null)}>
              Keep them
            </button>
          </div>
        </div>
      ) : null}
      <div className="sheet-body">
        {items.length === 0 ? (
          <Empty
            title="Nothing is waiting for review."
            body={
              live.projectSlug
                ? 'No ticket in this project is in Human Review. Choose all projects in the top bar to see the rest.'
                : 'No ticket in any project you can see is in Human Review.'
            }
          />
        ) : (
          <div className="sheet-panel">
            <table className="table review-queue-table">
              <thead>
                <tr>
                  <th className="review-queue-check">
                    <input
                      type="checkbox"
                      aria-label="Select every ticket"
                      checked={allSelected}
                      disabled={working}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((item) => item.ticketId)))}
                    />
                  </th>
                  <th>Ticket</th>
                  <th>Waiting</th>
                  <th>Risk</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.ticketId}
                    className={needsCloseLook(item) ? 'review-queue-row is-look' : 'review-queue-row'}
                    data-ticket={item.key}
                  >
                    <td className="review-queue-check">
                      <input
                        type="checkbox"
                        aria-label={`Select ${item.key}`}
                        checked={selected.has(item.ticketId)}
                        disabled={working}
                        onChange={() =>
                          setSelected((current) => {
                            const next = new Set(current);
                            if (next.has(item.ticketId)) next.delete(item.ticketId);
                            else next.add(item.ticketId);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td className="review-queue-ticket">
                      <button type="button" className="review-queue-open" onClick={() => onOpenTicket(item.key)}>
                        <span className="mono review-queue-key">{item.key}</span>
                        <span className="review-queue-title">{item.title}</span>
                      </button>
                      <span className="dim review-queue-project">
                        {item.projectName} · {item.category}
                        {item.mrIid !== null ? (
                          item.mrUrl ? (
                            <>
                              {' · '}
                              <a href={item.mrUrl} target="_blank" rel="noreferrer">
                                !{item.mrIid}
                              </a>
                            </>
                          ) : (
                            ` · !${item.mrIid}`
                          )
                        ) : null}
                      </span>
                    </td>
                    <td className="num" title={absoluteTime(item.since)}>
                      {observedTime(item.since, live.tick, format)}
                    </td>
                    <td className="review-queue-risk">
                      <span className="visually-hidden">{reviewRiskSummary(item)}</span>
                      <span aria-hidden className="review-queue-facts">
                        <span className={item.changedLines === null || item.binary ? 'is-warn' : undefined}>{sizeLabel(item)}</span>
                        <span className={item.sensitivePaths === null || item.sensitivePaths.length > 0 ? 'is-warn' : undefined}
                          title={item.sensitivePaths?.map((row) => row.path).join('\n')}
                        >
                          {sensitiveLabel(item)}
                        </span>
                        <span className={item.bounces > 0 ? 'is-warn' : undefined}>{bounceLabel(item)}</span>
                        <span className={item.reviewers.some((row) => row.verdict !== 'pass' || row.errors > 0) ? 'is-warn' : undefined}>
                          {reviewerLabel(item)}
                        </span>
                      </span>
                      {item.autoMerge.declined && item.autoMerge.declined.length > 0 ? (
                        <span className="review-queue-policy dim">Not auto-approved: {item.autoMerge.declined.join('; ')}</span>
                      ) : null}
                    </td>
                    <td className="right review-queue-actions">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpenTicket(item.key)}>
                        Review
                      </button>
                      <button
                        type="button"
                        className="btn btn-pink btn-sm"
                        disabled={working}
                        onClick={() => void approveOne(item)}
                        aria-label={`Approve ${item.key}`}
                      >
                        {busy.has(item.ticketId) ? 'Approving…' : 'Approve'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
